// Secrets shown once (brand v2, D121): a token, a key or a signing secret is shown whole, always
// with Copy, and closing before it was copied asks first, since it is never shown again.
import { useCallback, useState } from 'react'
import { useI18n } from '../i18n'
import { copyText } from './d/Toast'
import { Icon } from './icons'

/** The secret, whole (it wraps), with a Copy button that says when it worked. */
export function SecretField({
  value,
  label,
  onCopied,
}: {
  value: string
  label: string
  onCopied?: () => void
}) {
  const { t } = useI18n()
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  return (
    <figure className="secret" aria-label={label}>
      <code className="secret-v" translate="no">
        {value}
      </code>
      <button
        type="button"
        className="btn sm"
        onClick={async () => {
          const ok = await copyText(value)
          setState(ok ? 'copied' : 'failed')
          if (ok) onCopied?.()
        }}
      >
        <Icon name={state === 'copied' ? 'check' : 'copy'} small />
        {state === 'copied' ? t('secret.copied') : t('secret.copy')}
      </button>
      {state === 'failed' ? (
        <p role="alert" className="secret-err">
          {t('secret.copyFailed')}
        </p>
      ) : null}
    </figure>
  )
}

/**
 * Whether the secret was copied, and a close that asks first when it was not. `close` is what
 * really closes (the dialog's); `tryClose` is what Done and Escape call.
 */
export function useSecretGate(close: () => void) {
  const [copied, setCopied] = useState(false)
  const [asking, setAsking] = useState(false)
  const reset = useCallback(() => {
    setCopied(false)
    setAsking(false)
  }, [])
  const tryClose = useCallback(() => {
    if (copied) {
      reset()
      close()
    } else setAsking(true)
  }, [copied, close, reset])
  const force = useCallback(() => {
    reset()
    close()
  }, [close, reset])
  return { copied, markCopied: () => setCopied(true), asking, tryClose, force, reset }
}

/** Done, and the question it asks when the secret was not copied yet. */
export function SecretDone({
  value,
  gate,
  doneLabel,
}: {
  value: string
  gate: ReturnType<typeof useSecretGate>
  doneLabel: string
}) {
  const { t } = useI18n()
  if (gate.asking)
    return (
      <div className="notice warn secret-ask" role="alert">
        <p>{t('secret.notCopied')}</p>
        <div className="head-actions">
          <button
            type="button"
            className="btn solid"
            onClick={async () => {
              if (await copyText(value)) gate.force()
            }}
          >
            {t('secret.copyAndClose')}
          </button>
          <button type="button" className="btn danger" onClick={gate.force}>
            {t('secret.closeAnyway')}
          </button>
        </div>
      </div>
    )
  return (
    <div className="head-actions">
      <button type="button" className="btn solid" onClick={gate.tryClose}>
        {doneLabel}
      </button>
    </div>
  )
}
